/**
 * Lead Time specific parsing logic
 */

import { readFile } from '../shared/fileReader';
import { normalizeLeadTimeDates } from '../shared/dateNormalizer';
import { addWeekField } from '../shared/weekCalculator';
import { formatDateSafe } from '../../dateUtils';
import { 
  validateHeaders as validateLeadTimeHeaders, 
  generateHeaderError as generateLeadTimeHeaderError, 
  generateValidationError as generateLeadTimeValidationError, 
  validateData as validateLeadTimeData 
} from '../../validations/leadTime/validator';

/**
 * Calculates lead time for an item
 * @param {Date} commitedDate - Committed date
 * @param {Date} closedDate - Closed date  
 * @returns {number} Lead time in days
 */
function calculateLeadTime(commitedDate, closedDate) {
  let leadTime = Math.ceil((closedDate - commitedDate) / (1000 * 60 * 60 * 24)) + 1;
  return leadTime < 1 ? 1 : leadTime;
}

/**
 * Processes a single lead time item
 * @param {Object} item - Raw item data
 * @returns {Object} Processed item with lead time calculations
 */
function processLeadTimeItem(item) {
  const { commitedDate, closedDate } = normalizeLeadTimeDates(item);
  const leadTime = calculateLeadTime(commitedDate, closedDate);
  
  const processedItem = {
    ...item,
    leadTime,
    commitedDate,
    closedDate,
    'Committed Date': formatDateSafe(commitedDate),
    'Closed Date': formatDateSafe(closedDate)
  };
  
  return addWeekField(processedItem);
}

/**
 * Parses a file for lead time analysis
 * @param {File} file - The file to parse
 * @returns {Array} Processed data array with lead time calculations
 */
export async function parseLeadTimeFile(file) {
  // Read file data
  const { headers, rawData } = await readFile(file);
  
  // Validate headers
  const missingHeaders = validateLeadTimeHeaders(headers);
  if (missingHeaders.length > 0) {
    throw new Error(generateLeadTimeHeaderError(missingHeaders, headers));
  }

  // Validate data
  const { validationErrors, validData } = validateLeadTimeData(rawData);
  if (validationErrors.length > 0) {
    throw new Error(generateLeadTimeValidationError(validationErrors));
  }

  // Process valid data
  return validData.map(processLeadTimeItem);
}
